import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { saveStudentSession, getStudentSession, clearStudentSession } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    Loader2, LogOut, Coins, Search, Calendar as CalendarIcon,
    LogIn as LogInIcon, ShieldCheck, XCircle, Video, ExternalLink, ArrowRight,
    RefreshCw, MessageSquare, Star, Briefcase, CalendarClock,
} from 'lucide-react';
import { STUDENT_DASHBOARD, MENTOR_CARD } from '@/constants/testIds';
import { formatDateTime, fromNow } from '@/lib/format';
import { BookingModal } from '@/components/BookingModal';
import { StudentFeedbackModal } from '@/components/StudentFeedbackModal';
import { BrandLogo } from '@/components/BrandLogo';
import dayjs from 'dayjs';

/* ------------------------------------------------------------------ *
 *  LOGIN SCREEN
 * ------------------------------------------------------------------ */
function LoginView({ onLoggedIn }) {
    const [form, setForm] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post('/student/login', {
                email: form.email.trim(),
                password: form.password,
            });
            saveStudentSession(data.token, data.student);
            onLoggedIn(data.student);
            toast.success(`Welcome, ${data.student.name || data.student.email}!`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sign-in failed. Please check your email + password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-orb px-4 py-10 sm:px-6">
            <div className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blob-lavender" />
            <div className="pointer-events-none absolute -bottom-24 right-0 h-96 w-96 rounded-full bg-blob-periwinkle blur-3xl" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <div className="inline-flex"><BrandLogo /></div>
                    <p className="mt-3 text-sm text-muted-foreground">Consulting mentorship for ambitious students</p>
                </div>

                <Card className="border-border/60 shadow-[0_20px_60px_rgba(12,12,44,0.08)]">
                    <CardContent className="p-8">
                        <div className="mb-4 flex items-center gap-2 rounded-full border border-accent/30 bg-secondary px-3 py-1 text-xs font-medium text-primary w-fit">
                            <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                            Verified student access
                        </div>
                        <h1 className="font-display text-2xl font-semibold tracking-tight">Sign in to book a mentor</h1>
                        <p className="mt-1 text-sm text-muted-foreground">Use your college email and the password you set from your invitation magic link.</p>

                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="email">College email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    placeholder="you@college.edu"
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                    data-testid={STUDENT_DASHBOARD.email}
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <a href="/student/forgot" className="text-xs text-primary hover:underline">Forgot password?</a>
                                </div>
                                <Input
                                    id="password"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    placeholder="Your password"
                                    value={form.password}
                                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                    data-testid="student-login-password-input"
                                />
                            </div>
                            <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid={STUDENT_DASHBOARD.submit}>
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogInIcon className="mr-2 h-4 w-4" />}
                                Sign in
                                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                            </Button>
                        </form>
                        <p className="mt-6 text-center text-xs text-muted-foreground">
                            First time here? Look for the invitation link your admin sent you.
                        </p>
                    </CardContent>
                </Card>

                <div className="mt-6 text-center">
                    <a href="/admin/login" className="text-xs text-muted-foreground underline decoration-dotted underline-offset-4 hover:text-foreground">
                        Admin? Sign in here
                    </a>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 *  MENTOR CARD (inline: simpler card, uses brand palette)
 * ------------------------------------------------------------------ */
function MentorTile({ mentor, onBook }) {
    const initials = mentor.name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
    const domains = (mentor.domain || '').split(',').map((d) => d.trim()).filter(Boolean);
    // Hide generic filler expertise tags from the card
    const HIDDEN_EXPERTISE = new Set(['career coaching', 'mentoring']);
    const expertise = (mentor.expertise || [])
        .filter((e) => !HIDDEN_EXPERTISE.has((e || '').trim().toLowerCase()))
        .slice(0, 6);
    return (
        <Card
            data-testid={MENTOR_CARD.root}
            className="card-lift group h-full overflow-hidden border-border/60 bg-card"
        >
            <CardContent className="flex h-full flex-col gap-5 p-6">
                <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16 border border-border/60">
                        <AvatarImage src={mentor.avatar_url} alt={mentor.name} />
                        <AvatarFallback className="bg-secondary text-primary">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-semibold text-primary">{mentor.name}</h3>
                        {domains.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1" data-testid="mentor-card-domain">
                                {domains.map((d) => (
                                    <Badge
                                        key={d}
                                        className="bg-primary/10 text-primary hover:bg-primary/15 border border-primary/20 font-medium"
                                    >
                                        {d}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{mentor.headline}</p>
                    </div>
                </div>
                {expertise.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="mentor-card-expertise">
                        {expertise.map((e) => (
                            <Badge key={e} variant="secondary" className="font-normal" data-testid="expertise-chip">
                                {e}
                            </Badge>
                        ))}
                    </div>
                )}
                <div className="mt-auto flex items-center justify-between border-t border-border/50 pt-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-3">
                        {(mentor.rating !== undefined && mentor.rating !== null) && (
                            <span className="flex items-center gap-1" data-testid="mentor-card-rating">
                                <Star className="h-3.5 w-3.5 fill-current text-amber-500" />
                                <span className="font-medium text-foreground tabular-nums">
                                    {typeof mentor.rating === 'number' ? mentor.rating.toFixed(1) : mentor.rating}
                                </span>
                            </span>
                        )}
                        {(mentor.years_experience !== undefined && mentor.years_experience !== null) && (
                            <span className="flex items-center gap-1" data-testid="mentor-card-experience">
                                <Briefcase className="h-3.5 w-3.5" />
                                <span className="tabular-nums">{mentor.years_experience} yrs exp</span>
                            </span>
                        )}
                    </div>
                    <Badge className="bg-accent text-accent-foreground hover:bg-accent">
                        <CalendarIcon className="mr-1 h-3 w-3" />
                        Available
                    </Badge>
                </div>
                <Button
                    data-testid={MENTOR_CARD.view}
                    onClick={onBook}
                    className="w-full"
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Book Session
                </Button>
            </CardContent>
        </Card>
    );
}

/* ------------------------------------------------------------------ *
 *  BOOKING ROW (upcoming/past)
 * ------------------------------------------------------------------ */
function BookingRow({ booking, onCancel, onReschedule, onFeedback, cancelHrs, rescheduleHrs }) {
    const initials = booking.mentor_name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
    const now = dayjs();
    const start = dayjs(booking.start_iso);
    const end = dayjs(booking.end_iso);
    const hoursUntilStart = start.diff(now, 'hour', true);

    const isScheduled = booking.status === 'scheduled';
    const isBeforeStart = isScheduled && start.isAfter(now);
    const isLive = isScheduled && !start.isAfter(now) && end.isAfter(now);
    const isUpcoming = isScheduled && end.isAfter(now); // covers both before-start and live
    const sessionEnded = end.isBefore(now);
    const canCancel = isBeforeStart && hoursUntilStart >= cancelHrs;
    const canReschedule = isBeforeStart && hoursUntilStart >= rescheduleHrs;
    const canFeedback = sessionEnded && booking.status !== 'cancelled';

    return (
        <div
            data-testid={STUDENT_DASHBOARD.booking_row}
            className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11 border border-border/60">
                    <AvatarImage src={booking.mentor_avatar_url} alt={booking.mentor_name} />
                    <AvatarFallback className="bg-secondary text-primary">{initials}</AvatarFallback>
                </Avatar>
                <div>
                    <div className="font-medium">{booking.mentor_name}</div>
                    <div className="text-xs text-muted-foreground">
                        {formatDateTime(booking.start_iso)} · {booking.duration_minutes} min
                        {booking.topic && <span className="ml-2 italic">· {booking.topic}</span>}
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {isLive && (
                    <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 animate-pulse">
                        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-white"></span>
                        Live now
                    </Badge>
                )}
                {isScheduled && isBeforeStart && (
                    <Badge className="bg-accent text-accent-foreground">Upcoming · {fromNow(booking.start_iso)}</Badge>
                )}
                {booking.status === 'cancelled' && <Badge variant="outline">Cancelled</Badge>}
                {booking.status === 'completed' && <Badge variant="secondary">Completed</Badge>}
                {isScheduled && !isUpcoming && booking.status !== 'completed' && (
                    <Badge variant="secondary">Past</Badge>
                )}
                {isScheduled && booking.meet_link && (
                    <a href={booking.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20">
                        <Video className="h-3.5 w-3.5" /> Meet
                    </a>
                )}
                {isScheduled && booking.google_event_link && (
                    <a href={booking.google_event_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20">
                        <ExternalLink className="h-3.5 w-3.5" /> Calendar
                    </a>
                )}
                {canReschedule && (
                    <Button
                        data-testid={STUDENT_DASHBOARD.reschedule_button}
                        variant="outline"
                        size="sm"
                        onClick={() => onReschedule(booking)}
                    >
                        <RefreshCw className="mr-1 h-4 w-4" /> Reschedule
                    </Button>
                )}
                {canCancel && (
                    <Button
                        data-testid={STUDENT_DASHBOARD.cancel_button}
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => onCancel(booking)}
                    >
                        <XCircle className="mr-1 h-4 w-4" /> Cancel
                    </Button>
                )}
                {canFeedback && (
                    <Button
                        data-testid={STUDENT_DASHBOARD.feedback_button}
                        variant={booking.student_feedback_submitted ? 'secondary' : 'default'}
                        size="sm"
                        onClick={() => onFeedback(booking)}
                    >
                        <MessageSquare className="mr-1 h-4 w-4" />
                        {booking.student_feedback_submitted ? 'View feedback' : 'Give feedback'}
                    </Button>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 *  AUTHENTICATED HOME: mentor list + tiny sidebar (credits + bookings)
 * ------------------------------------------------------------------ */
function AuthedHome({ student, onLogout, refreshStudent }) {
    const navigate = useNavigate();
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [domainFilter, setDomainFilter] = useState('all');
    const [bookings, setBookings] = useState([]);
    const [bookingMentor, setBookingMentor] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [rescheduleBooking, setRescheduleBooking] = useState(null);
    const [feedbackBooking, setFeedbackBooking] = useState(null);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [policy, setPolicy] = useState({ cancel_cutoff_hours: 12, reschedule_cutoff_hours: 12 });
    const [topTab, setTopTab] = useState('book');

    const loadAll = async () => {
        setLoading(true);
        try {
            const [m, b, s] = await Promise.all([
                api.get('/mentors'),
                api.get('/student/bookings'),
                api.get('/settings/public'),
            ]);
            setMentors(m.data.mentors || []);
            setBookings(b.data.bookings || []);
            setPolicy(s.data || policy);
        } catch (e) {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    // Split a mentor's domain string into individual tags, e.g. "Consulting, Finance" -> ["Consulting", "Finance"]
    const parseDomains = (m) =>
        (m.domain || '')
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean);

    // Build unique, sorted list of individual domains across all mentors
    const availableDomains = Array.from(
        new Set(mentors.flatMap((m) => parseDomains(m)))
    ).sort((a, b) => a.localeCompare(b));

    const filtered = mentors.filter((m) => {
        // Domain filter (a mentor matches if the selected domain is one of its tags)
        if (domainFilter !== 'all' && !parseDomains(m).includes(domainFilter)) return false;
        // Search filter
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            m.name.toLowerCase().includes(q) ||
            m.headline?.toLowerCase().includes(q) ||
            m.expertise?.some((e) => e.toLowerCase().includes(q)) ||
            m.consulting_firm?.toLowerCase().includes(q) ||
            (m.domain || '').toLowerCase().includes(q)
        );
    });

    const upcoming = bookings.filter((b) => b.status === 'scheduled' && dayjs(b.end_iso).isAfter(dayjs()));
    const past = bookings.filter((b) => b.status !== 'scheduled' || !dayjs(b.end_iso).isAfter(dayjs()));

    const cancelBooking = async (b) => {
        if (!window.confirm(`Cancel this booking? You must cancel at least ${policy.cancel_cutoff_hours}h before the session. Your credit will be refunded.`)) return;
        try {
            const { data } = await api.post(`/bookings/${b.id}/cancel`);
            toast.success(data.credit_refunded ? 'Booking cancelled and credit refunded.' : 'Booking cancelled.');
            loadAll();
            refreshStudent();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cancel failed.');
        }
    };

    const openReschedule = (b) => {
        // Find mentor object for the modal
        const mentor = mentors.find((m) => m.id === b.mentor_id) || {
            id: b.mentor_id,
            name: b.mentor_name,
            avatar_url: b.mentor_avatar_url,
        };
        setBookingMentor(mentor);
        setRescheduleBooking(b);
        setModalOpen(true);
    };

    const openFeedback = (b) => {
        setFeedbackBooking(b);
        setFeedbackOpen(true);
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Top bar */}
            <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
                <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
                    <BrandLogo />
                    <div className="flex items-center gap-2">
                        <div className="hidden items-center gap-2 rounded-full border border-border/60 bg-secondary px-3 py-1.5 text-xs font-medium text-primary sm:flex">
                            <Coins className="h-3.5 w-3.5 text-accent" />
                            <span data-testid={STUDENT_DASHBOARD.credits} className="tabular-nums">{student?.credits ?? 0}</span>
                            <span className="text-muted-foreground">credits</span>
                        </div>
                        <div className="hidden text-right text-xs sm:block">
                            <div className="font-medium text-foreground">{student?.name || student?.email}</div>
                            <div className="text-muted-foreground">{student?.college_id}</div>
                        </div>
                        <Button
                            data-testid={STUDENT_DASHBOARD.logout}
                            variant="ghost"
                            size="sm"
                            onClick={onLogout}
                        >
                            <LogOut className="mr-1 h-4 w-4" /> Log out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
                <Tabs value={topTab} onValueChange={setTopTab} className="w-full">
                    {/* Top toggle: Book a Session / Your Sessions */}
                    <TabsList className="mb-8 h-auto w-full max-w-md gap-1 rounded-full bg-secondary p-1">
                        <TabsTrigger
                            value="book"
                            data-testid="tab-book-session"
                            className="flex-1 rounded-full py-2 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm"
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" /> Book a Session
                        </TabsTrigger>
                        <TabsTrigger
                            value="sessions"
                            data-testid="tab-your-sessions"
                            className="flex-1 rounded-full py-2 data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm"
                        >
                            <CalendarClock className="mr-2 h-4 w-4" /> Your Sessions
                            {bookings.length > 0 && (
                                <span
                                    className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-5 text-primary-foreground"
                                    data-testid="your-sessions-count"
                                >
                                    {bookings.length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    {/* ---------- BOOK A SESSION ---------- */}
                    <TabsContent value="book" className="mt-0 focus-visible:outline-none">
                        {/* Welcome hero (compact, no external nav noise) */}
                        <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                            <div>
                                <div className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary px-3 py-1 text-xs text-primary">
                                    Hi {student?.name?.split(' ')[0] || 'there'}, pick a mentor to book
                                </div>
                                <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-primary sm:text-4xl">
                                    Choose your mentor
                                </h1>
                                <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                                    Book a 1:1 session with a top-tier consulting mentor. 1 credit per session · cancel or reschedule outside the admin cutoff window.
                                </p>
                            </div>
                            <div className="relative w-full sm:w-72">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    placeholder="Search by name, firm, or topic…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Domain filter chips — only shown if any mentors have a domain tag */}
                        {availableDomains.length > 0 && (
                            <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="domain-filter">
                                <span className="mr-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Domain:</span>
                                <button
                                    type="button"
                                    onClick={() => setDomainFilter('all')}
                                    data-testid="domain-filter-all"
                                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                        domainFilter === 'all'
                                            ? 'border-primary bg-primary text-primary-foreground'
                                            : 'border-border bg-card text-foreground hover:bg-accent/40'
                                    }`}
                                >
                                    All ({mentors.length})
                                </button>
                                {availableDomains.map((d) => {
                                    const count = mentors.filter((m) => parseDomains(m).includes(d)).length;
                                    const active = domainFilter === d;
                                    return (
                                        <button
                                            key={d}
                                            type="button"
                                            onClick={() => setDomainFilter(d)}
                                            data-testid={`domain-filter-chip-${d}`}
                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                                active
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-border bg-card text-foreground hover:bg-accent/40'
                                            }`}
                                        >
                                            {d} ({count})
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Mentor grid */}
                        {loading ? (
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <Skeleton key={i} className="h-80 w-full rounded-xl" />
                                ))}
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border/60 bg-card p-12 text-center text-muted-foreground">
                                {search
                                    ? `No mentors match “${search}”${domainFilter !== 'all' ? ` in ${domainFilter}` : ''}.`
                                    : domainFilter !== 'all'
                                        ? `No mentors available under ${domainFilter} yet.`
                                        : 'No mentors available yet. Check back soon.'}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                {filtered.map((m) => (
                                    <MentorTile
                                        key={m.id}
                                        mentor={m}
                                        onBook={() => { setBookingMentor(m); setModalOpen(true); }}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* ---------- YOUR SESSIONS ---------- */}
                    <TabsContent value="sessions" className="mt-0 focus-visible:outline-none">
                        <section>
                            <h2 className="font-display text-xl font-semibold text-primary">Your sessions</h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Manage your upcoming sessions and revisit past ones — including feedback.
                            </p>
                            {loading ? (
                                <div className="mt-4 space-y-3">
                                    {Array.from({ length: 2 }).map((_, i) => (
                                        <Skeleton key={i} className="h-20 w-full rounded-xl" />
                                    ))}
                                </div>
                            ) : bookings.length === 0 ? (
                                <div className="mt-4 rounded-xl border border-dashed border-border/60 bg-card p-12 text-center">
                                    <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground/50" />
                                    <p className="mt-3 text-sm text-muted-foreground">You haven't booked any sessions yet.</p>
                                    <Button className="mt-4" onClick={() => setTopTab('book')} data-testid="empty-book-cta">
                                        <CalendarIcon className="mr-2 h-4 w-4" /> Book your first session
                                    </Button>
                                </div>
                            ) : (
                                <Tabs defaultValue="upcoming" className="mt-4">
                                    <TabsList>
                                        <TabsTrigger value="upcoming">Upcoming ({upcoming.length})</TabsTrigger>
                                        <TabsTrigger value="past">Past ({past.length})</TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="upcoming" className="mt-4 space-y-3">
                                        {upcoming.length === 0 ? (
                                            <div className="rounded-xl border border-dashed border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
                                                No upcoming sessions.
                                            </div>
                                        ) : (
                                            upcoming.map((b) => (
                                                <BookingRow
                                                    key={b.id}
                                                    booking={b}
                                                    onCancel={cancelBooking}
                                                    onReschedule={openReschedule}
                                                    onFeedback={openFeedback}
                                                    cancelHrs={policy.cancel_cutoff_hours}
                                                    rescheduleHrs={policy.reschedule_cutoff_hours}
                                                />
                                            ))
                                        )}
                                    </TabsContent>
                                    <TabsContent value="past" className="mt-4 space-y-3">
                                        {past.length === 0 ? (
                                            <div className="rounded-xl border border-dashed border-border/60 bg-card p-8 text-center text-sm text-muted-foreground">
                                                Your past bookings will show here.
                                            </div>
                                        ) : (
                                            past.map((b) => (
                                                <BookingRow
                                                    key={b.id}
                                                    booking={b}
                                                    onCancel={cancelBooking}
                                                    onReschedule={openReschedule}
                                                    onFeedback={openFeedback}
                                                    cancelHrs={policy.cancel_cutoff_hours}
                                                    rescheduleHrs={policy.reschedule_cutoff_hours}
                                                />
                                            ))
                                        )}
                                    </TabsContent>
                                </Tabs>
                            )}
                        </section>
                    </TabsContent>
                </Tabs>
            </main>

            {/* Direct booking + reschedule modal */}
            <BookingModal
                mentor={bookingMentor}
                open={modalOpen}
                rescheduleBooking={rescheduleBooking}
                domains={availableDomains}
                onOpenChange={(v) => {
                    setModalOpen(v);
                    if (!v) {
                        setBookingMentor(null);
                        setRescheduleBooking(null);
                    }
                }}
                onBooked={() => {
                    loadAll();
                    refreshStudent();
                    // Jump to "Your Sessions" so the freshly booked session is visible
                    setTopTab('sessions');
                }}
            />

            {/* Post-session feedback modal */}
            <StudentFeedbackModal
                booking={feedbackBooking}
                open={feedbackOpen}
                onOpenChange={(v) => {
                    setFeedbackOpen(v);
                    if (!v) setFeedbackBooking(null);
                }}
                onSubmitted={() => loadAll()}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ *
 *  ROUTE COMPONENT
 * ------------------------------------------------------------------ */
export default function StudentHomePage() {
    const [session, setSession] = useState(getStudentSession());
    const [student, setStudent] = useState(session?.student || null);

    const refreshStudent = async () => {
        try {
            const { data } = await api.get('/student/me');
            setStudent(data);
            // Keep the cached student fresh (e.g. credits) without dropping the token
            const tok = getStudentSession()?.token;
            if (tok) saveStudentSession(tok, data);
        } catch (e) {
            // Only log out on a genuine auth failure (invalid/expired token).
            // Transient errors (timeout, 5xx, network) must NOT drop a valid session,
            // otherwise a slow backend logs the candidate out on refresh.
            const status = e?.response?.status;
            if (status === 401 || status === 403) {
                clearStudentSession();
                setSession(null);
                setStudent(null);
            }
            // else: keep the existing session + cached student info
        }
    };

    useEffect(() => {
        if (session) refreshStudent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!session) {
        return (
            <LoginView
                onLoggedIn={(s) => {
                    setSession(getStudentSession());
                    setStudent(s);
                }}
            />
        );
    }

    return (
        <AuthedHome
            student={student}
            refreshStudent={refreshStudent}
            onLogout={() => {
                clearStudentSession();
                setSession(null);
                setStudent(null);
            }}
        />
    );
}
